// Profile data model & local store (ARCHITECTURE.md §4).
//
// The user's own ProfileRecord is the source of truth and lives locally in the
// clear (it is the user's own data). Every edit bumps a monotonic version — the
// number recipients use to accept only strictly newer updates.
//
// Avatars are deferred: the UI renders initials on a colour derived client-side
// (see avatar.ts). No image is stored or transmitted yet.

import { idbGet, idbPut, STORE_IDENTITY } from './db';

export interface Address {
  // Free-text label distinguishing entries when there's more than one
  // (e.g. 'Domicile', 'Travail') — optional, blank is fine for a single address.
  label: string;
  // Free-form, user-written lines (street, city, postal code, region — in
  // whatever order and format the writer's own country uses). No fixed
  // schema, since address formalism varies by country and the address
  // owner knows their own convention best.
  lines: string;
  country: string;
}

export interface Profile {
  firstName: string;
  lastName: string;
  dob: string; // free text, format YYYY/MM/DD, optional value ('' when unset)
  email: string;
  phone: string;
  addresses: Address[];
}

export interface ProfileRecord {
  version: number; // monotonic, +1 per edit
  updatedAt: number; // epoch ms
  profile: Profile;
}

const PROFILE_KEY = 'profile';

export function emptyProfile(): Profile {
  return {
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    phone: '',
    addresses: [],
  };
}

export function fullName(p: Profile): string {
  return [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
}

/**
 * Whether a profile carries nothing at all. A received one in this state is a
 * revocation tombstone (§9): the sender blanked their data on purpose, so it
 * must not be cached as if it were a real profile — that is what used to leave
 * a contact reading "connexion mutuelle" with every field empty.
 */
export function isEmptyProfile(p: Profile): boolean {
  return (
    !p.firstName && !p.lastName && !p.dob && !p.email && !p.phone && p.addresses.length === 0
  );
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/**
 * Coerce whatever shape a Profile was stored/received in — including ones
 * from before `addresses` existed — into the current shape. Applied at every
 * point a Profile is read back (local storage, a friend's cache, a restored
 * self-backup), so an old record never crashes the UI and its data isn't lost.
 */
export function normalizeProfile(raw: unknown): Profile {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    firstName: str(r.firstName),
    lastName: str(r.lastName),
    dob: str(r.dob),
    email: str(r.email),
    phone: str(r.phone),
    addresses: normalizeAddresses(r),
  };
}

function normalizeAddresses(r: Record<string, unknown>): Address[] {
  if (Array.isArray(r.addresses)) {
    return r.addresses
      .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object')
      .map((a) => ({ label: str(a.label), lines: str(a.lines), country: str(a.country) }))
      .filter((a) => a.label || a.lines || a.country);
  }
  // Pre-`addresses` records had a single `address` object instead of a list.
  const a = r.address;
  if (!a || typeof a !== 'object') return [];
  const ao = a as Record<string, unknown>;
  if ('lines' in ao || 'country' in ao) {
    const lines = str(ao.lines);
    const country = str(ao.country);
    return lines || country ? [{ label: '', lines, country }] : [];
  }
  // Original shape, predating the free-text address too: { street, city, state, zip }.
  const lines = [str(ao.street), [str(ao.zip), str(ao.city)].filter(Boolean).join(' '), str(ao.state)]
    .filter(Boolean)
    .join('\n');
  return lines ? [{ label: '', lines, country: '' }] : [];
}

/** Load the local ProfileRecord, or a fresh empty one at version 0. */
export async function getMyRecord(): Promise<ProfileRecord> {
  const stored = await idbGet<ProfileRecord>(STORE_IDENTITY, PROFILE_KEY);
  if (!stored) return { version: 0, updatedAt: 0, profile: emptyProfile() };
  return { ...stored, profile: normalizeProfile(stored.profile) };
}

/**
 * Persist a ProfileRecord verbatim, preserving its version. Used to restore the
 * profile from an encrypted self-backup on the relay after a recovery, where the
 * version must be kept exactly (not bumped) so it stays in sync with friends.
 */
export async function putMyRecord(record: ProfileRecord): Promise<void> {
  await idbPut(STORE_IDENTITY, PROFILE_KEY, record);
}

/** Persist an edited profile, bumping the version. Returns the new record. */
export async function saveMyProfile(profile: Profile): Promise<ProfileRecord> {
  const previous = await getMyRecord();
  const record: ProfileRecord = {
    version: previous.version + 1,
    updatedAt: Date.now(),
    profile,
  };
  await idbPut(STORE_IDENTITY, PROFILE_KEY, record);
  return record;
}

/**
 * Bump the profile version without changing its data. Used by revocation to
 * rotate the content key (a fresh key is minted on the next publish) and to
 * anchor a tombstone at a strictly newer version.
 */
export async function touchVersion(): Promise<ProfileRecord> {
  const previous = await getMyRecord();
  const record: ProfileRecord = { ...previous, version: previous.version + 1, updatedAt: Date.now() };
  await idbPut(STORE_IDENTITY, PROFILE_KEY, record);
  return record;
}
